import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, EMPTY, finalize, map, startWith, switchMap } from 'rxjs';
import { ReasonGroupService, ReasonService } from 'cbd-proxies';
import {
  ActionSelectOption,
  dismissDirtyFormModal,
  REASON_GROUP_NAME_STAND_DEACTIVATION,
  ZendraLocalizationServices,
  ZnConfirmation,
} from '@zendra/shared';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { StandPlanningOrchestratorService } from 'projects/orchestrator/src/lib/proxy/orchestrator/controllers/stand-planning-orchestrator.service';
import { StandDeactivateInputDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand/models';
import { ZnSingleSelect } from '../../../../../shared/src/lib/zn-select/zn-single-select';

@Component({
  selector: 'lib-action-deactivate-stand',
  imports: [LocalizationPipe, CommonModule, ReactiveFormsModule, ZnSingleSelect],
  templateUrl: './action-deactivate-stand.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionDeactivateStand implements IModalContent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly orchestratorService = inject(StandPlanningOrchestratorService);
  private readonly reasonService = inject(ReasonService);
  private readonly reasonGroupService = inject(ReasonGroupService);
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  selectedRecords = input<any[]>([]);
  selectedRecordIds = input<number[]>([]);

  loading = signal(true);
  saving = signal(false);
  reasonOptions = signal<ActionSelectOption[]>([]);

  localizationResource = ZendraLocalizationServices.LanguageService;
  entityResource = ZendraLocalizationServices.Planning;

  form: FormGroup = this.formBuilder.group({
    deactivationReasonId: [null, [Validators.required]],
  });

  private formStatusChanges() {
    return this.form.statusChanges.pipe(startWith(this.form.status));
  }

  private readonly formStatus = toSignal(this.formStatusChanges(), {
    initialValue: this.form.status,
  });

  canSave = computed(() => {
    this.formStatus();
    return this.form.valid && !this.loading();
  });

  constructor() {
    this.loadReasons();
  }

  fieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  save() {
    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.saving()) {
      return;
    }

    const standId = this.selectedRecordIds()[0];
    if (standId === undefined || standId === null) {
      return;
    }

    this.saving.set(true);

    const request: StandDeactivateInputDto = {
      standId: Number(standId),
      deactivationReasonId: Number(this.form.get('deactivationReasonId').value),
    };

    this.orchestratorService
      .deactivateStand(request)
      .pipe(
        catchError(error => {
          console.error(error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe(response => {
        this.toaster.success(`${this.localizationResource}::Actions:SuccessMessage`, null, {
          life: 5000,
          messageLocalizationParams: [
            this.localizationService.instant(
              `${ZendraLocalizationServices.Planning}::Action.Modal.DeactivateStand`,
            ),
          ],
        });
        this.activeModal.close(response);
      });
  }

  confirm(): void {
    this.save();
  }

  dismiss(): void {
    dismissDirtyFormModal(
      this.form,
      this.saving(),
      this.confirmationService,
      this.activeModal,
    );
  }

  private loadReasons(): void {
    this.loading.set(true);

    this.reasonGroupService
      .getList({
        name: REASON_GROUP_NAME_STAND_DEACTIVATION,
        maxResultCount: 1,
      })
      .pipe(
        map(response => {
          const reasonGroupId = response.items?.[0]?.id;

          if (!reasonGroupId) {
            throw new Error(`${this.localizationResource}::ReasonGroupNotFound`);
          }

          return reasonGroupId;
        }),
        switchMap(reasonGroupId =>
          this.reasonService.getList({
            reasonGroupId: [reasonGroupId],
            maxResultCount: 100,
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: reasons => {
          this.reasonOptions.set(this.toSelectOptions(reasons.items ?? []));
          this.loading.set(false);
        },
        error: error => {
          console.error(error);
          this.toaster.error(`${this.localizationResource}::ErrorLoadingCatalogs`, '', {
            life: 5000,
          });
          this.loading.set(false);
        },
      });
  }

  private toSelectOptions<T extends { id?: number; name?: string | null }>(
    collection: T[],
  ): ActionSelectOption[] {
    return collection.flatMap(item =>
      item.id === undefined
        ? []
        : [
            {
              key: item.id,
              value: item.name ?? '',
            },
          ],
    );
  }
}
